"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { cn, iso, withAlpha } from "@routecore/core";

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

/**
 * Compact popover calendar used by the "Day" period filter.
 * `markers` maps yyyy-MM-dd → count, rendering an activity dot per day.
 */
export default function DatePicker({
  value,
  onChange,
  markers,
  accent = "#E4572E",
}: {
  value: Date;
  onChange: (d: Date) => void;
  markers?: Map<string, number>;
  accent?: string;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => startOfMonth(value));
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Re-sync the visible month whenever the selection changes externally.
  useEffect(() => setView(startOfMonth(value)), [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(view), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(view), { weekStartsOn: 1 });
    const out: Date[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) out.push(d);
    return out;
  }, [view]);

  const today = new Date();

  const pick = (d: Date) => {
    onChange(d);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Pick a date"
        className={cn(
          "inline-flex items-center gap-2 rounded-full border py-2 pr-3 pl-3.5 text-sm font-bold whitespace-nowrap shadow-sm transition-all active:scale-[0.98]",
          open
            ? "border-ink bg-ink text-porcelain"
            : "border-line bg-porcelain text-ink hover:border-ink/30",
        )}
      >
        <CalendarDays className={cn("size-4", open ? "text-porcelain" : "text-accent")} />
        {format(value, "EEE, MMM d")}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="absolute top-full left-0 z-50 mt-2 w-[282px] origin-top-left rounded-2xl border border-line bg-porcelain p-3 shadow-lifted"
          >
            {/* month nav */}
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setView((v) => subMonths(v, 1))}
                className="grid size-7 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-cream hover:text-ink"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="font-display text-sm font-semibold tracking-tight">
                {format(view, "MMMM yyyy")}
              </span>
              <button
                type="button"
                onClick={() => setView((v) => addMonths(v, 1))}
                className="grid size-7 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-cream hover:text-ink"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>

            {/* weekday header */}
            <div className="grid grid-cols-7 gap-0.5">
              {WEEKDAYS.map((w, i) => (
                <div
                  key={`${w}-${i}`}
                  className="grid h-6 place-items-center text-[9px] font-bold tracking-wider text-ink-faint uppercase"
                >
                  {w}
                </div>
              ))}
            </div>

            {/* day grid */}
            <div className="grid grid-cols-7 gap-0.5">
              {days.map((d) => {
                const key = iso(d);
                const selected = isSameDay(d, value);
                const isToday = isSameDay(d, today);
                const inMonth = isSameMonth(d, view);
                const count = markers?.get(key) ?? 0;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => pick(d)}
                    title={count > 0 ? `${count} itinerar${count === 1 ? "y" : "ies"}` : undefined}
                    className={cn(
                      "relative grid h-9 place-items-center rounded-lg text-xs font-bold transition-all active:scale-90",
                      selected
                        ? "bg-ink text-porcelain shadow-card"
                        : inMonth
                          ? "text-ink hover:bg-cream"
                          : "text-ink-faint/55 hover:bg-cream/60",
                      !selected && isToday && "ring-1 ring-accent ring-inset",
                    )}
                  >
                    {format(d, "d")}
                    {count > 0 && (
                      <span
                        className="absolute bottom-1 size-1 rounded-full"
                        style={{
                          backgroundColor: selected ? "rgba(253,251,246,0.9)" : accent,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* footer */}
            <div className="mt-2 flex items-center justify-between border-t border-line/70 pt-2">
              <span className="flex items-center gap-1.5 text-[10px] font-semibold text-ink-faint">
                <span
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: withAlpha(accent, 0.9) }}
                />
                has itineraries
              </span>
              <button
                type="button"
                onClick={() => pick(new Date())}
                className="rounded-full px-2.5 py-1 text-[11px] font-bold text-accent transition-colors hover:bg-accent/10"
              >
                Today
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
